import { z } from "zod";
import type { ProposalInput, ProposalPlan } from "./proposals/types";

/**
 * Insurance completion package (spec §9): a certificate of completion NMT issues (the homeowner
 * doesn't sign it), and a final insurance invoice summary for the carrier (paperwork only; nothing
 * goes to QuickBooks).
 * Amounts in cents.
 */

export interface CompletionSummary {
  /** NMT's contract work per the approved estimate (RCV, code upgrades included). */
  contractRcvCents: number;
  /** Supplements the carrier approved (supplement change orders). */
  supplementsCents: number;
  /** Everything insured that NMT completed. */
  totalCompletedCents: number;
  /** ACV for NMT's work, the homeowner's deductible included (already covered). */
  acvCents: number;
  deductibleCents: number;
  /** Depreciation the policy doesn't release (the homeowner's). */
  nonRecoverableCents: number;
  /** Released on completion: recoverable depreciation, code upgrades (Ordinance or Law) incurred, and supplements. */
  recoverableCents: number;
  codeUpgradeCents: number;
  /** What NMT asks the carrier to release now. */
  requestedCents: number;
}

/**
 * The carrier's numbers from the signed proposal's plan and the approved supplements. They tie out:
 * total completed = ACV + non-recoverable + requested.
 */
export function completionSummary(plan: ProposalPlan, supplementsCents: number): CompletionSummary {
  if (!Number.isInteger(supplementsCents) || supplementsCents < 0) throw new Error("Supplements must be whole cents, 0 or more.");
  const requestedCents = plan.contractRecDepCents + plan.contractPwiCents + supplementsCents;
  const s: CompletionSummary = {
    contractRcvCents: plan.contractRcvCents,
    supplementsCents,
    totalCompletedCents: plan.contractRcvCents + supplementsCents,
    acvCents: plan.contractAcvDueCents,
    deductibleCents: plan.deductibleDueCents,
    nonRecoverableCents: plan.contractNonRecDepCents,
    recoverableCents: plan.contractRecDepCents,
    codeUpgradeCents: plan.contractPwiCents,
    requestedCents,
  };
  if (s.acvCents + s.nonRecoverableCents + s.requestedCents !== s.totalCompletedCents) throw new Error("The completion summary doesn't tie out to the proposal.");
  return s;
}

/** The scope as listed on the certificate: each group heading and its lines. */
export function completionScope(input: Pick<ProposalInput, "scope" | "system">): { heading: string; items: string[] }[] {
  return input.scope.map((g) => ({ heading: g.heading, items: g.items.map((i) => i.description) }));
}

/** Draft while the numbers can change; issued once the certificate and summary are made (frozen). */
export type CompletionStatus = "draft" | "issued";

export interface CompletionItem {
  id: string;
  jobId: string;
  status: CompletionStatus;
  completedOn: string;
  carrierEmail: string;
  note: string;
  summary: CompletionSummary;
  scope: { heading: string; items: string[] }[];
  contractCents: number;
  claim: { carrier: string; claimNumber: string; policyNumber: string; dateOfLoss: string | null };
  customerName: string;
  address: string;
  /** When it was issued, and who issued it (named on the certificate). */
  issuedAt: string | null;
  issuedBy: string | null;
  certificateUrl: string | null;
  summaryUrl: string | null;
  depreciationReceivedAt: string | null;
  /** The carrier email, ready to paste (the CRM doesn't send email yet). */
  email: { to: string; subject: string; body: string };
}

export const completionUpdateSchema = z.object({
  completedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  carrierEmail: z.string().trim().max(254).optional(),
  note: z.string().trim().max(2000).optional(),
});

/** The carrier email (subject and body) for the package. */
export function completionEmail(a: { customerName: string; address: string; carrier: string; claimNumber: string; policyNumber: string; completedOn: string; requestedCents: number; companyName: string; senderName: string; phone: string }) {
  const usd = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
  const when = new Date(`${a.completedOn}T12:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  return {
    subject: `Claim ${a.claimNumber || "(no claim number)"}: work complete, ${a.customerName}`,
    body: [
      `Hello,`,
      ``,
      `${a.companyName} completed the work on claim ${a.claimNumber || "(no claim number)"}${a.policyNumber ? ` (policy ${a.policyNumber})` : ""} for ${a.customerName} at ${a.address} on ${when}.`,
      ``,
      `Attached are our certificate of completion and final invoice summary. Please release the remaining ${usd(a.requestedCents)} (recoverable depreciation, code upgrades incurred, and approved supplements) and let us know if you need anything else.`,
      ``,
      `Thank you,`,
      a.senderName,
      a.companyName,
      a.phone,
    ].join("\n"),
  };
}
